import { useGuildDetails, usePageInfo } from 'app/frontend/base/config/queries'
import { GuildApplicationForm } from './ApplicationForm'
import GuildContext from 'app/frontend/base/config/context/guild'
import { Progress } from '@/components/ui/progress'
import NotFound from 'app/frontend/base/pages/Error/NotFound'
import {
  defaultLegacyFormColors,
  legacyBackgroundStyle,
  urlToMainSite,
} from '../utils'
import usePrefetchedStore from 'app/frontend/base/state/usePrefetchedStore'
import useBasePath from 'app/frontend/base/hooks/useBasePath'
import TeamRecruitmentDetails from 'app/frontend/types/custom/TeamRecruitmentDetails'
import { ChevronLeft } from 'lucide-react'
import { Link, Route, Routes } from 'react-router-dom'
import HeaderDropdown from 'app/frontend/base/pages/Home/components/HeaderDropdown'

const LegacyApplicationForm = () => {
  const {
    data: { user },
  } = usePageInfo()
  const { data: guild, isPending } = useGuildDetails()
  const { getData } = usePrefetchedStore()
  const baseTeamPath = useBasePath('team')
  const team = getData<wowaudit.Team>(baseTeamPath)

  if (isPending) {
    return (
      <Progress
        className='-mx-8 w-full rounded-none bg-transparent h-1'
        indeterminate
      />
    )
  }

  if (!guild) {
    return <NotFound />
  }

  const details: Partial<TeamRecruitmentDetails> =
    team?.recruitmentDetails ?? {}

  const styles = {
    css_background:
      details.css_background ?? defaultLegacyFormColors.css_background,
    css_body: details.css_body ?? defaultLegacyFormColors.css_body,
    css_headers: details.css_headers ?? defaultLegacyFormColors.css_headers,
    css_text: details.css_text ?? defaultLegacyFormColors.css_text,
    css_inputs: details.css_inputs ?? defaultLegacyFormColors.css_inputs,
    css_header_bar:
      details.css_header_bar ?? defaultLegacyFormColors.css_header_bar,
  }

  return (
    <div
      className='flex justify-center min-h-screen'
      style={{
        ...legacyBackgroundStyle(styles.css_background),
        color: styles.css_text,
      }}
    >
      <main
        className='md:my-16 rounded-xl p-6 max-w-3xl w-full'
        style={legacyBackgroundStyle(styles.css_body)}
      >
        <div className='flex justify-center'>
          {details.logo ? (
            <img src={details.logo} alt={guild.name} className='h-16' />
          ) : (
            <span
              className='text-3xl font-bold'
              style={{ color: styles.css_headers }}
            >
              {guild.name}
            </span>
          )}
        </div>

        <div
          className='flex items-center justify-between px-6 my-8 -ml-6 w-[calc(100%+3rem)] h-12 text-accent-foreground'
          style={{ background: styles.css_header_bar }}
        >
          <Link
            to={details.website ?? urlToMainSite(baseTeamPath ?? '')}
            className='flex items-center gap-2 text-base'
          >
            <ChevronLeft size={18} /> Back to website
          </Link>

          {user ? <HeaderDropdown hideCharacterContext={true} /> : null}
        </div>

        <h2 className='font-medium' style={{ color: styles.css_headers }}>
          Apply to {guild.name}
        </h2>

        <GuildContext.Provider value={{ guild }}>
          <Routes>
            <Route
              path='/guild/:region/:realm/:guild/teams/:team/apply/:applicationId'
              element={
                <GuildApplicationForm inDashboard={false} editing={true} />
              }
            />
            <Route
              path='/guild/:region/:realm/:guild/teams/:team'
              element={
                <GuildApplicationForm inDashboard={false} editing={false} />
              }
            />
            <Route
              path='/guild/:region/:realm/:guild'
              element={
                <GuildApplicationForm inDashboard={false} editing={false} />
              }
            />
            <Route path='*' element={<NotFound />} />
          </Routes>
        </GuildContext.Provider>
      </main>
    </div>
  )
}

export default LegacyApplicationForm
